import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useLayoutEffect,
} from "react";

// ui
import {
  Paper,
  IconButton,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ReplayIcon from "@mui/icons-material/Replay";

import { dataDifine, DataColumn, TableColumn } from "../config/dataInterface";

// jotai
import { useAtomValue } from "jotai";
import { AppAtoms } from "../lib/store";

import { CONST } from "../config/const";
import { generatePageRange } from "./Table";

// ========================================
// 型定義
// ========================================

interface RunkingTableTitleProps {
  serchOpen: boolean;
  pageList: React.ReactNode[];
  displayNumber: number;
  setDisplayNumber: (displayNumber: number) => void;
  displayNumbers: number[];
  pageNumber: number;
  setPageNumber: (pageNumber: number) => void;
  maxPageNumber: number;
  tableTitle: string;
  getTableData: (edit: boolean) => void;
  totalCount?: number;
}

interface TableRunkingProps {
  getTableData: (edit: boolean) => void;
  serchOpen: boolean;
  tableColumn: TableColumn;
  dataColumn: DataColumn[];
  rows: dataDifine[]; // 統合データ（日別・月別が1レコードに含まれる）
  dailyDateLabel: string;
  monthlyDateLabel: string;
  displayNumber: number;
  setDisplayNumber: (displayNumber: number) => void;
  displayNumbers: number[];
  pageNumber: number;
  setPageNumber: (pageNumber: number) => void;
  maxPageNumber: number;
  tableTitle: string;
  sortColumn: { key: string; sort: string }[];
  setSortColumn: (sortColumn: { key: string; sort: string }[]) => void;
  productName: string;
  totalCount?: number;
  singleMode?: "daily" | "monthly";
}

// ========================================
// カラム定義
// ========================================

interface AccountColumnDef {
  key: string;
  width: number;
}

const accountColumnDefs: AccountColumnDef[] = [
  { key: "product_name", width: 120 },
  { key: "sale_company_name", width: 150 },
  { key: "authority", width: 130 },
  { key: "sale_company_group_name", width: 130 },
  { key: "sale_account_name", width: 130 },
];

interface MiddleHeaderGroup {
  name: string;
  columns: string[];
}

const middleHeaderGroups: MiddleHeaderGroup[] = [
  {
    name: "トスアップ",
    columns: [
      "toss_up_number",
      "toss_up_valid_appoint_number",
      "toss_up_pre_check_ok_number",
      "toss_up_pre_check_ok_rate",
      "toss_up_open_number",
    ],
  },
  {
    name: "クロージング",
    columns: [
      "toss_up_closing_appoint_number",
      "toss_up_closing_success_number",
      "toss_up_closing_success_rate",
      "toss_up_closing_success_open_number",
    ],
  },
  {
    name: "エントリー",
    columns: [
      "entry_number_shot",
      "entry_shot_open_number",
      "entry_number_stack",
      "entry_stack_open_number",
      "entry_cancell_number",
    ],
  },
];

// ========================================
// 定数
// ========================================

const MAJOR_HEADER_HEIGHT = 36;
const MIDDLE_HEADER_HEIGHT = 32;
const COLUMN_HEADER_HEIGHT = 32;
const SCROLLBAR_WIDTH = 17; // 縦スクロールバーの幅

// ========================================
// メインコンポーネント
// ========================================

export function TableRunking(props: TableRunkingProps) {
  const {
    getTableData,
    serchOpen,
    tableColumn,
    dataColumn,
    rows,
    dailyDateLabel,
    monthlyDateLabel,
    displayNumber,
    setDisplayNumber,
    displayNumbers,
    pageNumber,
    setPageNumber,
    maxPageNumber,
    tableTitle,
    sortColumn,
    setSortColumn,
    productName,
    totalCount,
    singleMode,
  } = props;

  const loginUser = useAtomValue(AppAtoms.loginUser);
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);

  // 横スクロール位置
  const [scrollX, setScrollX] = useState(0);
  const bottomScrollRef = useRef<HTMLDivElement>(null);

  // 日別セクションの幅を測定するためのref
  const dailySectionRef = useRef<HTMLDivElement>(null);
  const [dailySectionWidth, setDailySectionWidth] = useState(0);

  // ページリスト
  const [pageList, setPageList] = useState<React.ReactNode[]>([]);
  useEffect(() => {
    const list: React.ReactNode[] = [];
    for (let i = 1; i <= maxPageNumber; i++) {
      list.push(
        <MenuItem key={"maxPageNumber" + i} value={i}>
          {i}
        </MenuItem>,
      );
    }
    setPageList(list);
  }, [maxPageNumber]);

  const [screenDataColumn, setScreenDataColumn] = useState<DataColumn[]>([]);
  useEffect(() => {
    setScreenDataColumn(
      dataColumn.length > 0 ? dataColumn : tableColumn.dataColumn,
    );
  }, [tableColumn, dataColumn]);

  // カラム情報取得
  const getColumnInfo = useCallback(
    (key: string): DataColumn | undefined => {
      return screenDataColumn.find((col) => col.key === key);
    },
    [screenDataColumn],
  );

  // カラム幅を数値で取得
  const getColumnWidth = useCallback(
    (key: string): number => {
      const col = getColumnInfo(key);
      return parseInt(col?.tableMinWidth || "100", 10);
    },
    [getColumnInfo],
  );

  // 権限チェック
  const hasAuth = useCallback(
    (column: DataColumn): boolean => {
      return (
        loginUser !== undefined &&
        column.tableDetailAuth.includes(loginUser.authority)
      );
    },
    [loginUser],
  );

  // 表示行数
  const maxRows = rows.length;

  // 表示可能カラム
  const visibleAccountColumns = accountColumnDefs.filter((colDef) => {
    const col = getColumnInfo(colDef.key);
    return col && col.table && hasAuth(col);
  });

  const visibleGroups = middleHeaderGroups
    .map((group) => ({
      ...group,
      visibleColumns: group.columns.filter((key) => {
        const col = getColumnInfo(key);
        return col && col.table && hasAuth(col);
      }),
    }))
    .filter((group) => group.visibleColumns.length > 0);

  // データ部分の総幅を計算（1セクション分）
  const dataColumnsWidth = visibleGroups.reduce((sum, group) => {
    return (
      sum +
      group.visibleColumns.reduce((groupSum, key) => {
        return groupSum + getColumnWidth(key);
      }, 0)
    );
  }, 0);

  // 日別セクションの幅を測定（ResizeObserverで常に監視）
  useLayoutEffect(() => {
    const element = dailySectionRef.current;
    if (!element) return;

    const updateWidth = () => {
      setDailySectionWidth(element.offsetWidth);
    };

    // 初回計算
    updateWidth();

    // ResizeObserverで要素のサイズ変更を監視
    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });
    resizeObserver.observe(element);

    // ウィンドウリサイズも監視
    window.addEventListener("resize", updateWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateWidth);
    };
  }, [visibleGroups, dataColumnsWidth]);

  // 横スクロール処理
  const handleBottomScroll = useCallback(() => {
    const scrollLeft = bottomScrollRef.current?.scrollLeft || 0;
    setScrollX(scrollLeft);
  }, []);

  // ソート（日別・月別のプレフィックス付きキーを受け取る）
  const handleSortClick = useCallback(
    (columnKey: string, type: "daily" | "monthly") => {
      // プレフィックス付きのキーを作成
      const prefixedKey = `${type}_${columnKey}`;
      const isPrimary = sortColumn[0]?.key === prefixedKey;
      const newSort = isPrimary && sortColumn[0].sort === "DESC" ? "ASC" : "DESC";
      // 同じカラムの日別・月別両方を削除してから新しいソートを追加
      let newSortColumn = sortColumn.filter(
        (c) =>
          c.key !== `daily_${columnKey}` && c.key !== `monthly_${columnKey}`,
      );
      newSortColumn.unshift({ key: prefixedKey, sort: newSort });
      setSortColumn(newSortColumn);
    },
    [sortColumn, setSortColumn],
  );

  // アカウント列の合計幅
  const accountTotalWidth = visibleAccountColumns.reduce(
    (sum, colDef) => sum + colDef.width,
    0,
  );

  // ========================================
  // ヘッダー部分のレンダリング
  // ========================================

  const renderAccountHeader = () => (
    <Box
      sx={{
        width: accountTotalWidth,
        minWidth: accountTotalWidth,
        borderRight: "2px solid var(--color-border, #e5e5e0)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* 大項目 */}
      <Box
        sx={{
          backgroundColor: "var(--color-ai)",
          borderBottom: "1px solid var(--color-border, #e5e5e0)",
          height: MAJOR_HEADER_HEIGHT,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box className="table-header" sx={{ fontWeight: "bold", fontSize: "var(--fs-11)", color: "var(--color-white)" }}>
          アカウント
        </Box>
      </Box>
      {/* 中項目（空白） */}
      <Box
        sx={{
          backgroundColor: "var(--color-ai)",
          borderBottom: "1px solid var(--color-border, #e5e5e0)",
          height: MIDDLE_HEADER_HEIGHT,
        }}
      />
      {/* 小項目 */}
      <Box
        sx={{
          backgroundColor: "var(--color-ai)",
          borderBottom: "1px solid var(--color-border, #e5e5e0)",
          height: COLUMN_HEADER_HEIGHT,
          display: "flex",
          alignItems: "center",
        }}
      >
        {visibleAccountColumns.map((colDef) => {
          const col = getColumnInfo(colDef.key)!;
          return (
            <Box
              key={`account-header-${colDef.key}`}
              sx={{
                width: colDef.width,
                minWidth: colDef.width,
                boxSizing: "border-box",
                padding: "4px 2px",
                textAlign: "left",
                borderRight: "1px solid var(--color-border-light, #efefea)",
                "&:last-child": { borderRight: "none" },
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <Box className="table-header" sx={{ fontSize: "var(--fs-11)", color: "var(--color-white)" }}>
                {col.name}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  // 日別・月別セクションの幅を計算（サブピクセル誤差を防ぐため明示的に指定）
  const sectionWidth = singleMode
    ? `calc(100% - ${accountTotalWidth}px)`
    : `calc((100% - ${accountTotalWidth}px) / 2)`;

  const renderDataHeader = (type: "daily" | "monthly", dateLabel: string) => (
    <Box
      ref={(type === "daily" || (type === "monthly" && singleMode === "monthly")) ? dailySectionRef : undefined}
      sx={{
        width: sectionWidth,
        display: "flex",
        flexDirection: "column",
        borderRight: (type === "daily" && !singleMode) ? "2px solid var(--color-border, #e5e5e0)" : "none",
        overflow: "hidden",
        minWidth: 0,
      }}
    >
      {/* 大項目 */}
      <Box
        sx={{
          backgroundColor: "var(--color-ai)",
          borderBottom: "1px solid var(--color-border, #e5e5e0)",
          height: MAJOR_HEADER_HEIGHT,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box className="table-header" sx={{ fontWeight: "bold", fontSize: "var(--fs-11)", color: "var(--color-white)" }}>
          {type === "daily"
            ? `日別（${dateLabel}）：${productName}`
            : `月別（${dateLabel}）：${productName}`}
        </Box>
      </Box>
      {/* 中項目 + 小項目（transformでスクロール） */}
      <Box
        sx={{
          overflow: "hidden",
          backgroundColor: "var(--color-ai)",
          borderBottom: "1px solid var(--color-border, #e5e5e0)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: dataColumnsWidth,
            transform: `translateX(-${scrollX}px)`,
          }}
        >
          {visibleGroups.map((group, groupIndex) => {
            const groupWidth = group.visibleColumns.reduce((sum, key) => {
              return sum + getColumnWidth(key);
            }, 0);

            return (
              <Box
                key={`${type}-group-${groupIndex}`}
                sx={{
                  width: groupWidth,
                  minWidth: groupWidth,
                  boxSizing: "border-box",
                  borderRight:
                    groupIndex < visibleGroups.length - 1
                      ? "2px solid var(--color-border, #e5e5e0)"
                      : "none",
                }}
              >
                {/* 中項目名 */}
                <Box
                  sx={{
                    textAlign: "left",
                    height: MIDDLE_HEADER_HEIGHT,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    borderBottom: "1px solid var(--color-border, #e5e5e0)",
                    fontWeight: "bold",
                  }}
                >
                  <Box className="table-header" sx={{ color: "var(--color-white)" }}>{group.name}</Box>
                </Box>
                {/* 小項目名 */}
                <Box
                  sx={{
                    display: "flex",
                    height: COLUMN_HEADER_HEIGHT,
                  }}
                >
                  {group.visibleColumns.map((key, colIndex) => {
                    const col = getColumnInfo(key)!;
                    const colWidth = getColumnWidth(key);
                    const shortName = col.name
                      .replace("トスアップ-", "")
                      .replace("クロージング-", "")
                      .replace("エントリー-", "");
                    // プレフィックス付きのソートキー
                    const prefixedKey = `${type}_${key}`;

                    return (
                      <Box
                        key={`${type}-col-header-${key}`}
                        sx={{
                          width: colWidth,
                          minWidth: colWidth,
                          boxSizing: "border-box",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          borderRight:
                            colIndex < group.visibleColumns.length - 1
                              ? "1px solid var(--color-border, #e5e5e0)"
                              : "none",
                        }}
                      >
                        <Box
                          className="table-header"
                          sx={{ fontSize: "11px", whiteSpace: "nowrap", color: "var(--color-white)" }}
                        >
                          {shortName}
                          {col.sort && (
                            <IconButton
                              sx={{
                                height: "18px",
                                width: "18px",
                                marginLeft: "2px",
                                padding: 0,
                                color: "var(--color-white)",
                                "&:hover": {
                                  backgroundColor: "rgba(255,255,255,0.12)",
                                },
                              }}
                              size="small"
                              onClick={() => handleSortClick(col.key, type)}
                            >
                              {sortColumn.some(
                                (c) =>
                                  c.key === prefixedKey && c.sort === "DESC",
                              ) ? (
                                <ArrowDropDownIcon sx={{ fontSize: 16 }} />
                              ) : (
                                <ArrowDropUpIcon sx={{ fontSize: 16 }} />
                              )}
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );

  // ========================================
  // データ行のレンダリング（1行分）
  // ========================================

  const renderRow = (rowIndex: number) => {
    const row = rows[rowIndex] || {};

    const rowBgColor =
      rowIndex % 2 === 1 ? CONST.tableBackgroundColor : "white";

    return (
      <Box
        key={`row-${rowIndex}`}
        sx={{
          display: "flex",
          borderBottom: "1px solid var(--color-border-light, #efefea)",
          minHeight: "32px",
        }}
      >
        {/* アカウント列 */}
        <Box
          sx={{
            width: accountTotalWidth,
            minWidth: accountTotalWidth,
            display: "flex",
            borderRight: "2px solid var(--color-border, #e5e5e0)",
            backgroundColor: rowBgColor,
          }}
        >
          {visibleAccountColumns.map((colDef) => (
            <Box
              key={`account-${rowIndex}-${colDef.key}`}
              sx={{
                width: colDef.width,
                minWidth: colDef.width,
                boxSizing: "border-box",
                padding: "4px 2px",
                textAlign: "left",
                borderRight: "1px solid var(--color-border-light, #efefea)",
                "&:last-child": { borderRight: "none" },
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <Box className="table-body" sx={{ fontSize: "12px" }}>
                {row[colDef.key] !== undefined
                  ? (row[colDef.key] as string)
                  : "-"}
              </Box>
            </Box>
          ))}
        </Box>
        {/* 日別データ */}
        {(!singleMode || singleMode === "daily") && (
          <Box
            sx={{
              width: sectionWidth,
              borderRight: singleMode ? "none" : "2px solid var(--color-border, #e5e5e0)",
              backgroundColor: rowBgColor,
              minWidth: 0,
              overflow: "hidden",
              display: "flex",
              alignItems: "stretch",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "stretch",
                width: dataColumnsWidth,
                transform: `translateX(-${scrollX}px)`,
              }}
            >
              {visibleGroups.map((group, groupIndex) =>
                group.visibleColumns.map((key, colIndex) => {
                  const colWidth = getColumnWidth(key);
                  const isLastInGroup =
                    colIndex === group.visibleColumns.length - 1;
                  const isLastGroup = groupIndex === visibleGroups.length - 1;
                  // 日別データはdaily_プレフィックス付きカラムから取得
                  const dailyKey = `daily_${key}`;

                  return (
                    <Box
                      key={`daily-${rowIndex}-${key}`}
                      sx={{
                        width: colWidth,
                        minWidth: colWidth,
                        boxSizing: "border-box",
                        padding: "4px 2px",
                        textAlign: "left",
                        borderRight:
                          isLastInGroup && !isLastGroup
                            ? "2px solid var(--color-border, #e5e5e0)"
                            : "1px solid var(--color-border-light, #efefea)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                      }}
                    >
                      <Box className="table-body" sx={{ fontSize: "12px" }}>
                        {row[dailyKey] !== undefined
                          ? (row[dailyKey] as string)
                          : "-"}
                      </Box>
                    </Box>
                  );
                }),
              )}
            </Box>
          </Box>
        )}
        {/* 月別データ */}
        {(!singleMode || singleMode === "monthly") && (
          <Box
            sx={{
              width: sectionWidth,
              backgroundColor: rowBgColor,
              minWidth: 0,
              overflow: "hidden",
              display: "flex",
              alignItems: "stretch",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "stretch",
                width: dataColumnsWidth,
                transform: `translateX(-${scrollX}px)`,
              }}
            >
              {visibleGroups.map((group, groupIndex) =>
                group.visibleColumns.map((key, colIndex) => {
                  const colWidth = getColumnWidth(key);
                  const isLastInGroup =
                    colIndex === group.visibleColumns.length - 1;
                  const isLastGroup = groupIndex === visibleGroups.length - 1;
                  // 月別データはmonthly_プレフィックス付きカラムから取得
                  const monthlyKey = `monthly_${key}`;

                  return (
                    <Box
                      key={`monthly-${rowIndex}-${key}`}
                      sx={{
                        width: colWidth,
                        minWidth: colWidth,
                        boxSizing: "border-box",
                        padding: "4px 2px",
                        textAlign: "left",
                        borderRight:
                          isLastInGroup && !isLastGroup
                            ? "2px solid var(--color-border, #e5e5e0)"
                            : "1px solid var(--color-border-light, #efefea)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                      }}
                    >
                      <Box className="table-body" sx={{ fontSize: "12px" }}>
                        {row[monthlyKey] !== undefined
                          ? (row[monthlyKey] as string)
                          : "-"}
                      </Box>
                    </Box>
                  );
                }),
              )}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  // ========================================
  // レンダリング
  // ========================================

  const pages = generatePageRange(pageNumber, maxPageNumber);
  const start = (pageNumber - 1) * displayNumber + 1;
  const end = rows ? start + rows.length - 1 : start + displayNumber - 1;

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", minHeight: 0, boxShadow: "var(--shadow-soft)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", backgroundColor: "var(--color-white)" }}>
      <Paper
        elevation={isSmartPhone ? undefined : 0}
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: "1 1 auto",
          minHeight: 0,
          marginTop: isSmartPhone ? "165px" : undefined,
          overflow: "hidden",
        }}
      >
        {/* ヘッダー + データ部分（縦スクロール：スクロールバーをヘッダーまで含めて表示） */}
        <Box
          sx={{
            flex: 1,
            overflowY: "scroll",
            overflowX: "hidden",
          }}
        >
          {/* ヘッダー部分（sticky で固定） */}
          <Box
            sx={{
              display: "flex",
              position: "sticky",
              top: 0,
              zIndex: 10,
            }}
          >
            {renderAccountHeader()}
            {(!singleMode || singleMode === "daily") && renderDataHeader("daily", dailyDateLabel)}
            {(!singleMode || singleMode === "monthly") && renderDataHeader("monthly", monthlyDateLabel)}
          </Box>
          {/* データ部分 */}
          {Array.from({ length: maxRows }, (_, i) => renderRow(i))}
        </Box>
        {/* 下部スクロールバー（テーブル全幅） */}
        <Box
          ref={bottomScrollRef}
          sx={{
            overflowX: "auto",
            overflowY: "hidden",
            flexShrink: 0,
          }}
          onScroll={handleBottomScroll}
        >
          {/* ダミーコンテンツ（スクロール範囲を作る） */}
          <Box
            sx={{
              // スクロールバー幅に対して、必要なスクロール分を追加
              width:
                dailySectionWidth > 0
                  ? `calc(100% + ${dataColumnsWidth - dailySectionWidth}px)`
                  : dataColumnsWidth,
              height: "1px",
            }}
          />
        </Box>
      </Paper>
      <Box
        data-testid="data-table-footer"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          px: 1,
          py: 0.75,
          borderTop: "1px solid var(--color-border, #e5e5e0)",
          bgcolor: "background.paper",
          flexShrink: 0,
          fontSize: "11px",
          color: "var(--color-sumi-muted)",
        }}
      >
        <Box component="span">
          {totalCount !== undefined ? `${totalCount}件中` : ""}
          {rows && rows.length > 0 ? `${start}〜${end}件を表示` : ""}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {pages.map((p, i) =>
            p === "..." ? (
              <Box component="span" key={`ellipsis-${i}`} sx={{ fontSize: "12px", px: 0.5 }}>
                ...
              </Box>
            ) : (
              <Box
                key={p}
                onClick={() => setPageNumber(p as number)}
                sx={{
                  cursor: "pointer",
                  minWidth: "28px",
                  height: "28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "4px",
                  fontSize: "12px",
                  fontWeight: p === pageNumber ? 600 : 400,
                  bgcolor: p === pageNumber ? "var(--color-ai)" : "transparent",
                  color: p === pageNumber ? "#fff" : "var(--color-sumi-light)",
                  "&:hover": {
                    bgcolor: p === pageNumber ? "var(--color-ai)" : "action.hover",
                  },
                }}
              >
                {p}
              </Box>
            ),
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box component="span">
            表示数:
          </Box>
          {displayNumbers.map((n) => (
            <Box
              key={n}
              onClick={() => setDisplayNumber(n)}
              sx={{
                cursor: "pointer",
                px: 0.75,
                py: 0.25,
                borderRadius: "4px",
                fontSize: "12px",
                fontWeight: n === displayNumber ? 600 : 400,
                color: "var(--color-sumi-light)",
                bgcolor: n === displayNumber ? "action.selected" : "transparent",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              {n}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default TableRunking;
